import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/Button';
import { Shell } from '../components/Shell';
import { uploadStore } from '../lib/uploadStore';
import { cropToBlob } from '../lib/cropImage';

export const Route = createFileRoute('/confirm')({
  component: ConfirmPage,
});

function ConfirmPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(() => uploadStore.get());
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageAspect, setImageAspect] = useState<number>(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);
  const croppedAreaRef = useRef<Area | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!file) {
      setImageSrc(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result as string;
      setImageSrc(src);
      const img = new Image();
      img.onload = () => setImageAspect(img.naturalWidth / img.naturalHeight);
      img.src = src;
    };
    reader.readAsDataURL(file);
  }, [file]);

  useEffect(() => {
    if (!file) void navigate({ to: '/' });
  }, [file, navigate]);

  const onCropComplete = useCallback((_: Area, area: Area) => {
    croppedAreaRef.current = area;
  }, []);

  function handleRetake(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.files?.[0];
    if (!next) return;
    uploadStore.set(next);
    setFile(next);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }

  async function handleConfirm() {
    if (!imageSrc || !croppedAreaRef.current || busy) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(imageSrc, croppedAreaRef.current);
      const cropped = new File([blob], 'framed.jpg', { type: 'image/jpeg' });
      uploadStore.set(cropped);
      await navigate({ to: '/analyzing' });
    } catch {
      setBusy(false);
    }
  }

  if (!file || !imageSrc) return null;

  return (
    <Shell showBack>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="pt-4 space-y-5"
      >
        <div>
          <h1 className="text-2xl font-black text-oud-900">{t('upload.title')}</h1>
          <p className="text-oud-600 mt-1 text-sm">{t('upload.hint')}</p>
        </div>

        <div className="relative rounded-3xl overflow-hidden shadow-quote border border-oud-100 bg-oud-900 min-h-[60vh]">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={imageAspect}
            showGrid={false}
            objectFit="contain"
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            classes={{ containerClassName: 'bg-oud-900' }}
          />
        </div>

        <div className="flex items-center gap-3 px-1" dir="ltr">
          <ZoomIcon className="w-5 h-5 text-oud-600" />
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            aria-label={t('upload.zoom')}
            className="flex-1 accent-oud-800 h-1"
          />
        </div>

        <div className="space-y-3">
          <Button size="lg" fullWidth onClick={handleConfirm} disabled={busy}>
            {t('upload.confirm')}
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleRetake}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="block w-full text-center text-sm text-oud-600 hover:text-oud-900 py-2"
          >
            {t('upload.retake')}
          </button>
        </div>
      </motion.div>
    </Shell>
  );
}

function ZoomIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3M8 11h6M11 8v6" />
    </svg>
  );
}
